import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import {
  getMainGenresAndSubGenres,
  getGenreSelectValueBySource,
} from '@intelligence/components/GenreSelectMiddleware/utils';
import {
  getGenreReqParamsForNew,
  getGenreReqParamsForOld,
} from '@intelligence/components/GenreSelectMiddleware/Supports/utils';
import { generateGenreSelectValue } from '@intelligence/v3/utils/genre';
import {
  getDefaultApiFieldMap,
  getDefaultUrlFieldMap,
  getItemApiParamsFieldMap,
  getItemUrlParamsFieldMap,
} from '../shared/fieldMapUtils';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';

import type { MainGenre, SubGenre } from '@intelligence/components/GenreSelectMiddleware/types';
import type { IFilterMiddlewareUtils } from '../shared/types';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IGenreFilterValue, IGenreSelectMiddlewareParams } from './types';

const GENRE_JOINER = '$$';

const getApiFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  genreField: string;
  subGenreField: string;
  genreSourceField: string;
  ieggGenreReqField: string;
  appannieGenreField: string;
  sensortowerGenreField: string;
  appmagicGenreField: string;
  minigameGenreField: string;
} => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);

  return {
    genreField: apiParamsFieldMap?.genre || defaultApiFieldMap.genre || 'genre',
    subGenreField: apiParamsFieldMap?.subGenre || defaultApiFieldMap.subGenre || 'sub_genre',
    genreSourceField: apiParamsFieldMap?.genreSource || defaultApiFieldMap.genreSource || 'genre_source',
    ieggGenreReqField: apiParamsFieldMap?.ieggGenreReq || defaultApiFieldMap.ieggGenreReq || 'iegg_genre_req',
    appannieGenreField: apiParamsFieldMap?.appannieGenre || defaultApiFieldMap.appannieGenre || 'appannie_genre',
    sensortowerGenreField:
      apiParamsFieldMap?.sensortowerGenre || defaultApiFieldMap.sensortowerGenre || 'sensortower_genre',
    appmagicGenreField: apiParamsFieldMap?.appmagicGenre || defaultApiFieldMap.appmagicGenre || 'appmagic_genre',
    minigameGenreField: apiParamsFieldMap?.minigameGenre || defaultApiFieldMap.minigameGenre || 'minigame_genre',
  };
};

const getUrlFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  genreSourceField: string;
  mainGenresField: string;
  subGenresField: string;
  level3GenresField: string;
  level4GenresField: string;
  level5GenresField: string;
} => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);

  return {
    genreSourceField: urlParamsFieldMap?.genreSource || defaultUrlFieldMap.genreSource || 'genreSource',
    mainGenresField: urlParamsFieldMap?.mainGenres || defaultUrlFieldMap.mainGenres || 'mainGenres',
    subGenresField: urlParamsFieldMap?.subGenres || defaultUrlFieldMap.subGenres || 'subGenres',
    level3GenresField: urlParamsFieldMap?.level3Genres || defaultUrlFieldMap.level3Genres || 'level3Genres',
    level4GenresField: urlParamsFieldMap?.level4Genres || defaultUrlFieldMap.level4Genres || 'level4Genres',
    level5GenresField: urlParamsFieldMap?.level5Genres || defaultUrlFieldMap.level5Genres || 'level5Genres',
  };
};

const getNormalizedGenreValue = (filterValue?: IGenreFilterValue): IGenreFilterValue | null => {
  if (isEmpty(filterValue?.source)) {
    return null;
  }

  const source = filterValue?.source || '';
  const genres = Array.isArray(filterValue?.genres) ? filterValue?.genres || [] : [];

  return {
    source,
    genres,
  };
};

const buildGenrePayload = (filterValue: IGenreFilterValue, params?: IGenreSelectMiddlewareParams) => {
  const sourceOptions = params?.options?.[filterValue.source] || [];
  const { mainGenres, subGenres, level3Genres, level4Genres, level5Genres } = getMainGenresAndSubGenres(
    filterValue,
    sourceOptions,
  );

  return {
    source: filterValue.source,
    options: sourceOptions,
    mainGenres,
    subGenres,
    level3Genres,
    level4Genres,
    level5Genres,
  };
};

const serializeMainGenre = (relation: string, value: string): string => {
  if (isEmpty(value)) {
    return '';
  }

  return `${relation || 'or'}${GENRE_JOINER}${value}`;
};

const serializeSubGenre = (...parts: Array<string | undefined>): string => {
  if (parts.some((item) => isEmpty(item))) {
    return '';
  }

  return parts.join(GENRE_JOINER);
};

const serializeGenreValues = <T>(values: T[] | undefined, serializer: (value: T) => string): string[] => {
  return (values || []).map(serializer).filter(isNotEmpty);
};

const deserializeMainGenres = (value: string): MainGenre[] => {
  return value
    .split(',')
    .map((itemValue) => {
      const [relation, genreValue] = itemValue.split(GENRE_JOINER);

      if (isEmpty(genreValue)) {
        return null;
      }

      return {
        relation: relation || 'or',
        value: genreValue,
        checked: false,
        indeterminate: false,
      };
    })
    .filter(isNotEmpty);
};

const deserializeSubGenres = (value: string, parser: (parts: string[]) => SubGenre | null): SubGenre[] => {
  return value
    .split(',')
    .map((itemValue) => {
      return parser(itemValue.split(GENRE_JOINER));
    })
    .filter(isNotEmpty);
};

const parseLevel2Genre = (parts: string[]): SubGenre | null => {
  const [parentValue, value] = parts;

  if (isEmpty(parentValue) || isEmpty(value)) {
    return null;
  }

  return {
    parentValue,
    value,
    checked: true,
  };
};

const parseLevel3Genre = (parts: string[]): SubGenre | null => {
  const [parentValue, parent2Value, value] = parts;

  if (isEmpty(parentValue) || isEmpty(parent2Value) || isEmpty(value)) {
    return null;
  }

  return {
    parentValue,
    parent2Value,
    value,
    checked: true,
  };
};

const parseLevel4Genre = (parts: string[]): SubGenre | null => {
  const [parentValue, parent2Value, parent3Value, value] = parts;

  if (isEmpty(parentValue) || isEmpty(parent2Value) || isEmpty(parent3Value) || isEmpty(value)) {
    return null;
  }

  return {
    parentValue,
    parent2Value,
    parent3Value,
    value,
    checked: true,
  };
};

const parseLevel5Genre = (parts: string[]): SubGenre | null => {
  const [parentValue, parent2Value, parent3Value, parent4Value, value] = parts;

  if (
    isEmpty(parentValue) ||
    isEmpty(parent2Value) ||
    isEmpty(parent3Value) ||
    isEmpty(parent4Value) ||
    isEmpty(value)
  ) {
    return null;
  }

  return {
    parentValue,
    parent2Value,
    parent3Value,
    parent4Value,
    value,
    checked: true,
  };
};

type GenreSelectApiParams = IRecord<any>;

class GenreSelectMiddlewareUtils
implements IFilterMiddlewareUtils<IGenreFilterValue, GenreSelectApiParams, IGenreSelectMiddlewareParams>
{
  convertToApiParams(
    item: IFilterItemConfig,
    filterValue?: IGenreFilterValue,
    params?: IGenreSelectMiddlewareParams,
  ): IRecord<any> | null {
    const normalizedValue = getNormalizedGenreValue(filterValue);

    if (!normalizedValue) {
      return null;
    }

    const payload = buildGenrePayload(normalizedValue, params);
    const fieldMap = getApiFieldMap(item, params);

    if (payload.source === GENRE_SOURCE_MAP.IEGG) {
      const genreReqParams = getGenreReqParamsForNew(payload);

      return {
        [fieldMap.genreSourceField]: genreReqParams?.source || '',
        [fieldMap.ieggGenreReqField]: genreReqParams?.genres || [],
      };
    }

    if (payload.source === GENRE_SOURCE_MAP.MINIGAME) {
      const genreReqParams = getGenreReqParamsForNew(payload);

      return {
        [fieldMap.genreSourceField]: genreReqParams?.source || '',
        [fieldMap.minigameGenreField]: genreReqParams?.genres || [],
      };
    }

    if (
      [GENRE_SOURCE_MAP.SENSORTOWER, GENRE_SOURCE_MAP.APP_MAGIC, GENRE_SOURCE_MAP.APP_ANNIE].includes(payload.source)
    ) {
      const genreReqParams = getGenreReqParamsForNew(payload);
      const sourceFieldMap = {
        [GENRE_SOURCE_MAP.SENSORTOWER]: fieldMap.sensortowerGenreField,
        [GENRE_SOURCE_MAP.APP_MAGIC]: fieldMap.appmagicGenreField,
        [GENRE_SOURCE_MAP.APP_ANNIE]: fieldMap.appannieGenreField,
      };
      const sourceGenresField = sourceFieldMap[payload.source] || '';

      return {
        [fieldMap.genreSourceField]: genreReqParams?.source || '',
        [sourceGenresField]: genreReqParams?.genres || [],
      };
    }

    const genreReqParams = getGenreReqParamsForOld(payload);

    return {
      [fieldMap.genreField]: genreReqParams?.mainGenres || [],
      [fieldMap.subGenreField]: genreReqParams?.subGenres || [],
      [fieldMap.genreSourceField]: genreReqParams?.source || '',
    };
  }

  serializeUrlParams(
    item: IFilterItemConfig,
    filterValue?: IGenreFilterValue,
    params?: IGenreSelectMiddlewareParams,
  ): IRecord<string> | null {
    const normalizedValue = getNormalizedGenreValue(filterValue);

    if (!normalizedValue) {
      return null;
    }

    const payload = buildGenrePayload(normalizedValue, params);
    const fieldMap = getUrlFieldMap(item, params);
    const result: IRecord<string> = {
      [fieldMap.genreSourceField]: payload.source,
    };

    const mainGenreValues = serializeGenreValues(payload.mainGenres, (itemValue) => {
      return serializeMainGenre(itemValue?.relation, itemValue?.value);
    });
    const subGenreValues = serializeGenreValues(payload.subGenres, (itemValue) => {
      return serializeSubGenre(itemValue?.parentValue, itemValue?.value);
    });
    const level3GenreValues = serializeGenreValues(payload.level3Genres, (itemValue) => {
      return serializeSubGenre(itemValue?.parentValue, itemValue?.parent2Value, itemValue?.value);
    });
    const level4GenreValues = serializeGenreValues(payload.level4Genres, (itemValue) => {
      return serializeSubGenre(
        itemValue?.parentValue,
        itemValue?.parent2Value,
        itemValue?.parent3Value,
        itemValue?.value,
      );
    });
    const level5GenreValues = serializeGenreValues(payload.level5Genres, (itemValue) => {
      return serializeSubGenre(
        itemValue?.parentValue,
        itemValue?.parent2Value,
        itemValue?.parent3Value,
        itemValue?.parent4Value,
        itemValue?.value,
      );
    });

    if (mainGenreValues.length > 0) {
      result[fieldMap.mainGenresField] = mainGenreValues.join(',');
    }
    if (subGenreValues.length > 0) {
      result[fieldMap.subGenresField] = subGenreValues.join(',');
    }
    if (level3GenreValues.length > 0) {
      result[fieldMap.level3GenresField] = level3GenreValues.join(',');
    }
    if (level4GenreValues.length > 0) {
      result[fieldMap.level4GenresField] = level4GenreValues.join(',');
    }
    if (level5GenreValues.length > 0) {
      result[fieldMap.level5GenresField] = level5GenreValues.join(',');
    }

    return result;
  }

  getManagedUrlKeys(item: IFilterItemConfig, params?: IGenreSelectMiddlewareParams): string[] {
    const fieldMap = getUrlFieldMap(item, params);

    return [
      fieldMap.genreSourceField,
      fieldMap.mainGenresField,
      fieldMap.subGenresField,
      fieldMap.level3GenresField,
      fieldMap.level4GenresField,
      fieldMap.level5GenresField,
    ].filter(isNotEmpty);
  }

  deserializeUrlParams(
    item: IFilterItemConfig,
    searchParams: URLSearchParams,
    params?: IGenreSelectMiddlewareParams,
  ): IGenreFilterValue | null {
    if (isEmpty(searchParams)) {
      return null;
    }

    const fieldMap = getUrlFieldMap(item, params);
    const source = searchParams.get(fieldMap.genreSourceField) || '';

    if (isEmpty(source)) {
      return null;
    }

    const mainGenresValue = searchParams.get(fieldMap.mainGenresField) || '';
    const subGenresValue = searchParams.get(fieldMap.subGenresField) || '';
    const level3GenresValue = searchParams.get(fieldMap.level3GenresField) || '';
    const level4GenresValue = searchParams.get(fieldMap.level4GenresField) || '';
    const level5GenresValue = searchParams.get(fieldMap.level5GenresField) || '';

    const mainGenres = deserializeMainGenres(mainGenresValue);
    const subGenres = deserializeSubGenres(subGenresValue, parseLevel2Genre);
    const level3Genres = deserializeSubGenres(level3GenresValue, parseLevel3Genre);
    const level4Genres = deserializeSubGenres(level4GenresValue, parseLevel4Genre);
    const level5Genres = deserializeSubGenres(level5GenresValue, parseLevel5Genre);

    if (isNotEmpty(params?.options?.[source])) {
      const sourceValue =
        getGenreReqParamsForNew({
          source,
          options: params?.options?.[source] || [],
          mainGenres,
          subGenres,
          level3Genres,
          level4Genres,
          level5Genres,
        }).genres || [];

      const genreSelectorValue = generateGenreSelectValue({
        source,
        sourceValue,
        optionsMap: params?.options || {},
      });

      return getGenreSelectValueBySource(genreSelectorValue);
    }

    return getGenreSelectValueBySource({
      source,
      mainGenres,
      subGenres,
      level3Genres,
      level4Genres,
      level5Genres,
    });
  }
}

export default GenreSelectMiddlewareUtils;
